import { postExternalConsumptionAPI } from "../utils/httpUtilsExternalAPI";
import { COPILOT_CONSTANTS } from "../components/copilot/constants";

/**
 * Uploads files as attachments to an existing ticket via the FILE_UPLOAD API endpoint.
 *
 * @param files - Array of File objects to upload
 * @param ticketId - The ticket/incident ID to attach files to
 * @param authClient - The authentication client (e.g., platformContext.authClient)
 * @returns The normalized API response
 */
export const processFilesOnly = async (
  files: File[],
  ticketId: string,
  authClient: any
) => {
  if (!ticketId) {
    throw new Error("Ticket number not found to attach files.");
  }

  const fileUploadApiUrl = COPILOT_CONSTANTS.API.ENDPOINTS.FILE_UPLOAD;
  const apiPayload = new FormData();
  apiPayload.append("ticketId", ticketId);
  apiPayload.append("confirmToAttachTicket", String(false));
  files.forEach((file) => {
    apiPayload.append("files", file, file.name);
  });

  const response = await postExternalConsumptionAPI(
    fileUploadApiUrl,
    apiPayload,
    authClient
  );

  // Normalize FILE_UPLOAD response payload (string or JSON) and surface its message
  if (
    response &&
    response.status >= COPILOT_CONSTANTS.API.SUCCESS_STATUS_MIN &&
    response.status < COPILOT_CONSTANTS.API.SUCCESS_STATUS_MAX
  ) {
    const rawPayload = response?.data ?? {};

    if (rawPayload.message) {
      return {
        ...response,
        data: {
          ...rawPayload,
          response: rawPayload.message,
          Message: rawPayload.message,
          format: "text",
        },
      };
    }
  }

  return response;
};

/**
 * HRD365 form names that send attachments inline with CreateTicket payload.
 * These forms route to the HRD365 createwithattachment endpoint on the backend,
 * which accepts file content directly in TicketCreationOptions.Attachments[].
 */
export const HRD_FORM_NAMES = [
  "Role & Incentive Navigator (RAIN) Help",
];

/**
 * Converts File objects to base64-encoded TicketAttachment objects for inline JSON submission.
 * Used by HRD365 forms to send files directly with the CreateTicket payload instead of
 * a separate UploadTicketAttachments call. The backend deserializes base64 → byte[] automatically.
 *
 * @param files - Array of File objects from file input
 * @returns Array of TicketAttachment-compatible objects with base64-encoded Content
 */
export const convertFilesToBase64Attachments = async (
  files: File[]
): Promise<{ FileName: string; Size: number; ContentType: string; Content: string }[]> => {
  const attachments: { FileName: string; Size: number; ContentType: string; Content: string }[] = [];

  for (const file of files) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      attachments.push({
        FileName: file.name,
        Size: file.size,
        ContentType: file.type || 'application/octet-stream',
        Content: base64,
      });
    } catch (err) {
      console.error(`[Attachment] Failed to read file ${file.name} as base64:`, err);
    }
  }

  return attachments;
};
